import { useQuery } from '@apollo/client';
import { IconButton, Text, Tooltip } from '@radix-ui/themes';
import { Columns3 } from 'lucide-react';
import { type ReactNode, use } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { GET_JOB_HEADER_DATA } from '../../behavior/queries/jobHeader';
import AddCandidateToJob from '../elements/AddCandidateToJob/AddCandidateToJob';
import Collaborators from '../elements/Collaborators';
import TeamtailorButton from '../elements/TeamtailorButton';
import Title from '../elements/Title';
import LocaleContext from '../hooks/LocaleContext';
import PageHeader from '../pages/PageHeader';
import StatisticsIcon from '../statistics/StatisticsIcon';
import JobPin from './JobPin';

const JobHeader = ({ jobId, extra }: { jobId?: string; extra?: ReactNode }) => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);
  const navigate = useNavigate();

  const { data, error, loading, refetch } = useQuery(GET_JOB_HEADER_DATA, {
    variables: {
      jobId,
    },
  });

  return (
    !loading &&
    !error && (
      <>
        <Title title={!loading ? data.job.title : t('jobHeader.titleFallback')} />
        <PageHeader className="swiper">
          <PageHeader.Swiper job={data.job} loading={loading} />
          {!loading && (
            <>
              <JobPin job={data.job} />
              <PageHeader.Buttons>
                <AddCandidateToJob jobId={jobId} />
                <Collaborators
                  users={data.job.collaborators.users}
                  groups={data.job.collaborators.accessGroups}
                  add={userSettings.company?.admin}
                  refetching={refetch}
                />
                {extra}
                <StatisticsIcon jobId={data.job.id} />
                <TeamtailorButton job={data.job} />
                <Tooltip content={<Text size="3">{t('jobHeader.kanbanTooltip')}</Text>}>
                  <IconButton
                    size="3"
                    color="gray"
                    variant="soft"
                    className="flex items-center justify-center ml-2"
                    onClick={() => navigate(`/job/${jobId}`)}
                  >
                    <Columns3 size={24} className="color dark-grey" />
                  </IconButton>
                </Tooltip>
              </PageHeader.Buttons>
            </>
          )}
        </PageHeader>
      </>
    )
  );
};

export default JobHeader;
